<template>
  <div class="brandingEdit">
    <div class="container">

      <div class="page-title" v-if="branding">
        <span class="name">{{$to(branding.name)}}</span>
        <button class="save btn btn-primary" @click="save" v-if="$store.getters.loggedUserCanSaveInventory">{{$t("save")}}</button>
      </div>

      <Tabs>
        <TabSection :title="$t('categories.information')" v-if="branding">
          <div class="columns">
            <div class="column">
              <div class="group">
                <LocalizedInput :title="$t('branding.name')" :translate="branding.name" :validate="$validate" errorsKey="branding.name" />
              </div>
              <div class="group">
                <label>{{$t('branding.color')}}</label>
                <div class="color-input">
                  <input type="color" v-model="branding.color">
                  <div class="color-value">{{branding.color}}</div>
                </div>
              </div>
              <div class="group">
                <LocalizedInput textarea :title="$t('branding.shortDescription')" :translate="branding.shortDescription" />
              </div>
            </div>
            <div class="column">
              <div class="group long">
                <LocalizedInput textarea :title="$t('branding.description')" :translate="branding.description" />
              </div>
            </div>
          </div>
        </TabSection>

        <TabSection :title="$t('order_config')">
          <div class="tabs">
            <div class="tab" v-for="method of activeMethods" :key="'tab-method' + method" @click="selectedMethod = method" :class="{active: selectedMethod === method}">
              {{$t('deliveryMethod.' + method)}}
            </div>
          </div>
          <template v-if="selectedMethodConfig">
            <br>
            <br>
            <div class="subtitle">{{$t('open_hours')}}</div>
            <div class="columns">
              <div class="column">
                <DayTimeRange :day="selectedMethodConfig.hours[day]" :dayName="day" :context="selectedMethod" max24
                              v-for="day of days.slice(0, 4)" :key="'day-' + day" @changed="selectedMethodConfig.hours[day] = $event"
                              :errorsKey="'config.' + selectedMethod + '.hours.' + day + '.hours'" :validate="$validate"></DayTimeRange>
              </div>
              <div class="column">
                <DayTimeRange :day="selectedMethodConfig.hours[day]" :dayName="day" :context="selectedMethod"
                              v-for="day of days.slice(4, 7)" :key="'day-' + day" max24
                              :errorsKey="'config.' + selectedMethod + '.hours.' + day + '.hours'" :validate="$validate"></DayTimeRange>
              </div>
            </div>
            <br>
            <div class="columns">
              <div class="column">
                <div class="subtitle">
                  <span class="flex-grow">{{$t("settings.delay_title")}}</span>
                </div>
                <div class="group">
                  <label>{{$t("settings.delay")}}</label>
                  <input type="number" v-model="selectedMethodConfig.general.averageDelay">
                </div>
              </div>
              <div class="column">

              </div>
            </div>
          </template>
        </TabSection>

        <TabSection :title="$t('images_config')">
          <div class="columns">
            <div class="column">
              <div class="subtitle">{{$t('branding.logo')}}</div>
              <div class="group">
                <ImageUpload identifier="brandingLogoUpload" :object="branding.logo" @changed="branding.logo = $event" :maxSizeInMb="0.8"
                             tooltip="branding.logo_tooltip"></ImageUpload>
              </div>
            </div>
            <div class="column">
              <div class="subtitle">{{$t('branding.horizontal_logo')}}</div>
              <div class="group">
                <ImageUpload identifier="brandingLogoUpload" :object="branding.horizontalLogo" @changed="branding.horizontalLogo = $event" :maxSizeInMb="0.8"
                             tooltip="branding.logo_horizontal_tooltip"></ImageUpload>
              </div>
            </div>
          </div>
          <div class="columns">
            <div class="column">
              <div class="subtitle">{{$t('branding.promo_image')}}</div>
              <div class="group">
                <ImageUpload identifier="brandingPromoImageUpload" :object="branding.promoImage" @changed="branding.promoImage = $event" :maxSizeInMb="0.8"
                             tooltip="kiosk_style.promo_tooltip"></ImageUpload>
              </div>
            </div>
            <div class="column">
              <div class="subtitle">{{$t('branding.banner_image')}}</div>
              <div class="group">
                <ImageUpload identifier="brandingBannerImageUpload" :object="branding.bannerImage" @changed="branding.bannerImage = $event" :maxSizeInMb="0.8"
                             tooltip="kiosk_style.banner_tooltip" ></ImageUpload>
              </div>
            </div>
          </div>

          <Panel class="slideshow" :title="$t('branding.banner_slideshow')">
            <div class="title">
              <div class="left">
                <div class="text">{{$t('branding.add_slideshow')}}</div>
                <div>{{$t('branding.add_slideshow_description')}}</div>
              </div>
              <button class="btn btn-primary" @click="addSlideshowImage">{{$t('add')}}</button>
            </div>
            <div class="slideshow-images">
              <div class="slideshow-image" v-for="(image, imageKey) of slideshowImages()" :key="'img-' + imageKey">
                <div class="name">{{$t('branding.slide')}} #{{imageKey + 1}}</div>
                <ImageUpload :identifier="'slideshowImage' + image.key" :object="image" :maxSizeInMb="1"
                             tooltip="kiosk_style.banner_tooltip"
                             @changed="slideshowImageChanged(image.key, $event)" :previewHeight="220"></ImageUpload>
              </div>
            </div>
          </Panel>

        </TabSection>

        <TabSection tag="SuperAdmin" :title="$t('settings.advanced_configurations') + ' POS'"
                    v-if="$store.getters.loggedUserHasSuperAdmin && branding.config && this.isMaitrePos">
          <div class="columns">
            <div class="column">
              <h5>Revenu Center</h5>
              <div class="group">
                <label>Online takeout</label>
                <input type="number" v-model="branding.config.externalPosOverwrites.online.takeout.center">
              </div>
              <div class="group">
                <label>Online delivery</label>
                <input type="number" v-model="branding.config.externalPosOverwrites.online.delivery.center">
              </div>
              <div class="group">
                <label>Online in place</label>
                <input type="number" v-model="branding.config.externalPosOverwrites.online.inPlace.center">
              </div>
              <div class="group">
                <label>Kiosk takeout</label>
                <input type="number" v-model="branding.config.externalPosOverwrites.kiosk.takeout.center">
              </div>
              <div class="group">
                <label>Kiosk delivery</label>
                <input type="number" v-model="branding.config.externalPosOverwrites.kiosk.delivery.center">
              </div>
              <div class="group">
                <label>Kiosk in place</label>
                <input type="number" v-model="branding.config.externalPosOverwrites.kiosk.inPlace.center">
              </div>
            </div>
            <div class="column">
              <h5>Mode</h5>
              <div class="group">
                <label>Online takeout</label>
                <input type="number" v-model="branding.config.externalPosOverwrites.online.takeout.mode">
              </div>
              <div class="group">
                <label>Online delivery</label>
                <input type="number" v-model="branding.config.externalPosOverwrites.online.delivery.mode">
              </div>
              <div class="group">
                <label>Online in place</label>
                <input type="number" v-model="branding.config.externalPosOverwrites.online.inPlace.mode">
              </div>
              <div class="group">
                <label>Kiosk takeout</label>
                <input type="number" v-model="branding.config.externalPosOverwrites.kiosk.takeout.mode">
              </div>
              <div class="group">
                <label>Kiosk delivery</label>
                <input type="number" v-model="branding.config.externalPosOverwrites.kiosk.delivery.mode">
              </div>
              <div class="group">
                <label>Kiosk in place</label>
                <input type="number" v-model="branding.config.externalPosOverwrites.kiosk.inPlace.mode">
              </div>
            </div>

          </div>
        </TabSection>

        <TabSection tag="SuperAdmin" :title="$t('settings.advanced_configurations') + ' POS'"
                    v-if="$store.getters.loggedUserHasSuperAdmin && branding.config && this.isVelocePos">
          <div class="columns">
            <div class="column">
              <h5>Veloce Overwrites</h5>
              <div class="group">
                <label>Takeout Selector</label>
                <input type="number" v-model="branding.config.externalPosOverwrites.takeoutSelector">
              </div>
              <div class="group">
                <label>Delivery Selector</label>
                <input type="number" v-model="branding.config.externalPosOverwrites.deliverySelector">
              </div>
              <div class="group">
                <label>In Place Selector</label>
                <input type="number" v-model="branding.config.externalPosOverwrites.inPlaceSelector">
              </div>
              <div class="group">
                <label>Remark Id</label>
                <input type="number" v-model="branding.config.externalPosOverwrites.remarkId">
              </div>
              <div class="group">
                <label>Table Number Remark Id</label>
                <input type="number" v-model="branding.config.externalPosOverwrites.tableNumberRemarkId">
              </div>
            </div>
          </div>
        </TabSection>
      </Tabs>

      <div class="footer-actions" v-if="$store.getters.loggedUserCanSaveInventory">
        <button class="btn btn-secondary" @click="back">{{$t('back')}}</button>
        <div class="grow"></div>
        <button class="btn btn-primary"  @click="save">{{$t('save')}}</button>
      </div>

    </div>
  </div>
</template>

<script>
  import { mapActions } from "vuex";
  import LocalizedInput from "@/components/tools/LocalizedInput";
  import DayTimeRange from '@/components/tools/DayTimeRange.vue';
  import ImageUpload from "@/components/tools/ImageUpload.vue";

  export default {
    name: "brandingEdit",

    components: { LocalizedInput, ImageUpload, DayTimeRange },

    data() {
      return {
        branding: {
          name: { fr: "", en: "" },
          description: { fr: "", en: "" },
          shortDescription: { fr: "", en: "" },
          logo: null,
          horizontalLogo: null,
          bannerImage: null,
          promoImage: null,
          images: {},
          config: null,
          color: "#fff",
          categories: [],
          externalPosOverwrites: null
        },
        externalPosData: null,
        selectedMethod: "",
        company: null,
        inventory: null,
        orderMethods: ["delivery","takeout","inPlace","catering","hotel"],
        days: ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"]
      }
    },

    async mounted() {
      this.$store.state.pageTitleTranslationKey = "inventory.categories";
      this.$store.state.pageIcon = "/icon/menu.svg";

      showSpinner();

      //Get External Pos for brands PosOverwrites
      this.getExternalPosData();

      this.inventory = await this.$store.getters.getContextInventory;
      this.company = await this.$store.getters.getContextCompany;
      if (this.brandingId) {
        await this.loadBranding();
      }
      this.validateConfig();
      if (this.$store.getters.isBranchContextInventory) {
        window.lockFields(this.$store.getters.getOverloadableFields);
      } else if (!this.$store.getters.loggedUserCanModifyInventory) {
        window.lockFields();
      }

      this.$nextTick(() => {
        this.selectedMethod = this.activeMethods[0];
      })
      hideSpinner();
    },

    computed: {
      brandingId() {
        return this.$route.query.id;
      },
      hasKioskModuleActivated() {
        return this.company && this.company.kiosk && this.company.kiosk.activated;
      },
      selectedMethodConfig() {
        if (!this.selectedMethod) {
          return;
        }
        return this.branding.config[this.selectedMethod];
      },
      activeMethods() {
        if (!this.company) {
          return [];
        }
        let methods = [];
        for (let method of this.orderMethods) {
          if (this.company.onlineOrdering[method].activated) {
            methods.push(method);
          }
        }
        return methods;
      },
      isMaitrePos() {
        return this.externalPosData ? (this.externalPosData.selected == "maitre" || this.externalPosData.selected == "sap")  : false
      },
      isVelocePos() {
        return this.externalPosData ? this.externalPosData.selected == "veloce" : false
      }
    },

    validate: {
      'branding.name.fr': {
        required: true,
        condition: function() {
          return !this.branding.name.en;
        }
      },
      'branding.name.en': {
        required: true,
        condition: function() {
          return !this.branding.name.fr;
        }
      }
    },
    watch: {
      "$store.getters.isBranchContextInventory"(newValue) {
        if (newValue) {
          window.lockFields(this.$store.getters.getOverloadableFields);
        }
      }
    },
    methods: {
      ...mapActions(["getBranding", "saveBranding", "createBranding", "saveImage", "getImage", "getCompanyExternalPos"]),

      back() {
        this.$router.push(this.$router.buildContext("/brandings"));
      },

      validateConfig() {
        if (!this.branding.config) {
          this.branding.config = {};
        }
        if (!this.branding.config.externalPosOverwrites) {
          this.branding.config.externalPosOverwrites = {
            online: {
              takeout: { center: null, mode: null },
              delivery: { center: null, mode: null },
              inPlace: { center: null, mode: null }
            },
            kiosk: {
              takeout: { center: null, mode: null },
              delivery: { center: null, mode: null },
              inPlace: { center: null, mode: null}
            }
          };
        }
        if (!this.branding.shortDescription) {
          this.branding.shortDescription = {fr: "", en: ""};
        }
        for (let method of this.orderMethods) {
          if (!this.branding.config[method]) {
            this.branding.config[method] = {
              general: {
                averageDelay: 0
              },
              hours: {}
            }
            for (let day of this.days) {
              this.branding.config[method].hours[day] = {
                hours: []
              }
            }
          }
        }
      },

      async loadBranding() {
        let response = await this.getBranding({inventoryId: this.inventory.id, brandingId: this.brandingId});
        if (response) {
          this.branding = response;
          if (!this.branding.images) {
            this.branding.images = {};
          }
        }
      },

      slideshowImages() {
        if (!this.branding.images) {
          return [];
        }
        let keys = Object.keys(this.branding.images);
        let images = [];
        for (let key of keys) {
          if (this.branding.images[key].tags.indexOf("slideshow") > -1) {
            this.branding.images[key].key = key;
            images.push(this.branding.images[key]);
          }
        }
        images.sort((first, second) => { return first.priority > second.priority });
        return images;
      },

      addSlideshowImage() {
        let uuid = generateUUID();
        this.branding.images[uuid] = {
          tags: ["slideshow"],
          image: { url: "" }
        }
        this.$forceUpdate();
      },

      async getExternalPosData(){
        this.companyId = this.$route.query.company;
        this.branchId = this.$route.query.branch;

        let external_posData = await this.getCompanyExternalPos({ companyId: this.companyId, branchId: this.branchId });
        if (external_posData) {
          this.externalPosData = external_posData;
          console.log('Pos -> ',  this.externalPosData);
        }

      },

      async save() {
        if (!this.$validate.valid()) {
          toast(this.$t("branch.error.required"), "warning");
          this.$validate.validateAllOnce();
          return;
        }
        showSpinner();
        let data = await this._formatBeforeSave();
        let response;
        if (this.brandingId) {
          response = await this.saveBranding({inventoryId: this.inventory.id, brandingId: this.brandingId, data: data});
        } else {
          response = await this.createBranding({inventoryId: this.inventory.id, data: data});
        }
        if (!response) {
          toast(this.$t("error.no_server"), "error");
          hideSpinner();
          return;
        }
        this.branding = response;
        if (!this.brandingId) {
          this.$router.push(this.$router.buildContext("/branding/edit", {id: this.branding.id}));
        }
        hideSpinner();
        toast(this.$t("branding.save_success"), "success", this.$t("success"));
      },

      async _formatBeforeSave() {
        let data = this._copy(this.branding);
        await this._formatImageBeforeSave(data, 'logo');
        await this._formatImageBeforeSave(data, 'horizontalLogo');
        await this._formatImageBeforeSave(data, 'bannerImage');
        await this._formatImageBeforeSave(data, 'promoImage');

        // Parse image data
        for (let key of Object.keys(data.images)) {
          let imageData;
          if (data.images[key].image.url === "" && !data.images[key].image.deleted) {
            imageData = null;
          } else {
            imageData = getImageData(data.images[key].image);
          }
          if (imageData === null) { // null img is to be deleted
            data.images[key] = null;
          } else {
            data.images[key] = {
              image:imageData,
              tags: data.images[key].tags
            }
          }
        }

        // Set image priority
        let index = 1;
        for (let key of Object.keys(data.images)) {
          if (data.images[key] === null) { // null img is to be deleted, do not count in priority
            continue;
          }
          data.images[key].priority = index;
          index++;
        }

        return data;
      },

      slideshowImageChanged(key, image) {
        this.branding.images[key].image = image;
        if (image.deleted) {
          this.branding.images[key].url = "";
        }
      },

      async _formatImageBeforeSave(obj, fieldName) {
        if (obj[fieldName] && obj[fieldName].url && obj[fieldName].url.startsWith("data:")) {
          let image = await this.saveImage({inventoryId: this.inventory.id, image: getImageData(obj[fieldName])});
          obj[fieldName] = image ? image.id : null;
        } else if (obj[fieldName] && obj[fieldName].id) {
          obj[fieldName] = obj[fieldName].id;
        } else if (obj[fieldName] && obj[fieldName].deleted) {
          obj[fieldName] = null;
        }
        return obj;
      },

      _copy(obj) {
        return JSON.parse(JSON.stringify(obj));
      }
    }
  }
</script>

<style lang="scss" scoped>
 .brandingEdit, .container {
   flex-grow: 1;
   display: flex;
   flex-direction: column;
 }
  .color-input {
    display: flex;
    align-items: center;

    .color-value {
      margin-left: 10px;
      font-family: 'Consolas', serif;
      color: #888;
      font-size: 14px;
    }
    input[type=color] {
      height: 40px;
    }
  }

  .slideshow {
    margin-top: 20px;
    .title {
      display: flex;
      justify-content: space-between;
      text-align: center;

      .left {
        text-align: left;
      }

      .text {
        font-weight: bold;
        font-size: 18px;
      }
    }
    .slideshow-image {
      border-top: 1px dashed #e6e6e6;
      padding-top: 20px;
      margin-top: 20px;
      .name {
        font-weight: bold;
        margin-bottom: 5px;
      }
    }
  }
</style>

<style lang="scss">
  .brandingEdit {
    .group.long {
      textarea {
        height: 15.5em;
      }
    }
  }
</style>
